import React, {useCallback} from "react";
import styled from "styled-components";
import {Groups} from "apps/DRAW_PREDICTOR/modules/enums";
import {SquadFlag} from "apps/DRAW_PREDICTOR/components";
import {useDispatch, useSelector} from "react-redux";
import {
	checkIsAllowedForEditLite,
	checkIsFullPredictions,
	getSquadByPositionAndGroupLite,
	getSquadForEditId,
} from "apps/DRAW_PREDICTOR/modules/selectors";
import {clearEditPredictionData, setPrediction1ForEdit} from "apps/DRAW_PREDICTOR/modules/actions";
import lockImg from "apps/DRAW_PREDICTOR/assets/images/lock.svg";
import {GroupName, LockImg, PredictionWrapper, SquadName} from "./Prediction";
import {ReactComponent as SwipeIcon} from "apps/DRAW_PREDICTOR/assets/images/icons/swipe.svg";
import SwipeWhiteIcon from "apps/DRAW_PREDICTOR/assets/images/icons/swipeWhite.svg";
import {media} from "assets/css/media";
import {useMediaQuery} from "modules";
import {IQualifierSquad} from "apps/DRAW_PREDICTOR/modules/reducers";
import {useTranslation} from "react-i18next";

const SwipeIconStyled = styled(SwipeIcon)`
	position: absolute;
	right: 10px;

	@media (max-width: ${media.mobile}) {
		right: auto;
		bottom: 10px;
	}
`;

const ActiveSwipeIconStyled = styled.div`
	position: absolute;
	height: 32px;
	width: 32px;
	background: url(${SwipeWhiteIcon}) no-repeat center center #ff004c;
	right: 10px;
	border-radius: 50%;
`;

interface IProps {
	group: Groups;
	position: number;
}

const checkIsLockChangeSquad = (lockFirstSquad: boolean, isFullPredictions: boolean) =>
	lockFirstSquad || !isFullPredictions;

const getSwipeIcon = (
	isLockChangeSquad: boolean,
	squadForEditId: number | null,
	currentSquadId: number | undefined
) => {
	if (squadForEditId && squadForEditId === currentSquadId) {
		return <ActiveSwipeIconStyled />;
	}

	if (!isLockChangeSquad) {
		return <SwipeIconStyled />;
	}

	return null;
};

const checkIsNotActive = (squadForEditId: number | null, currentSquadId: number | undefined) =>
	Boolean(squadForEditId) && squadForEditId !== currentSquadId;

export const SelectedPrediction: React.FC<IProps> = ({group, position}) => {
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);
	const squadForEditId = useSelector(getSquadForEditId);
	const selectedSquad = useSelector(getSquadByPositionAndGroupLite)(
		position,
		group
	) as IQualifierSquad;
	const predictionAllowedForEdit = useSelector(checkIsAllowedForEditLite)(
		selectedSquad?.id || 0,
		selectedSquad?.pot || 0
	);
	const isFullPredictions = useSelector(checkIsFullPredictions);
	const lockFirstSquad = group === "a" && position === 1;
	const isCanEditPrediction = Boolean(squadForEditId) && predictionAllowedForEdit;
	const isLockChangeSquad = checkIsLockChangeSquad(lockFirstSquad, isFullPredictions);
	const {i18n} = useTranslation();

	const handleSetPredictionForEdit = useCallback(() => {
		if (!selectedSquad || !isFullPredictions) {
			return;
		}
		if (squadForEditId && squadForEditId === selectedSquad.id) {
			dispatch(clearEditPredictionData());
		} else {
			dispatch(setPrediction1ForEdit(selectedSquad.id));
		}
	}, [dispatch, selectedSquad, isFullPredictions, squadForEditId]);

	return (
		<PredictionWrapper
			lockFirstSquad={lockFirstSquad}
			disabled={isLockChangeSquad}
			isEditThisSquad={squadForEditId === selectedSquad?.id}
			isNotActive={checkIsNotActive(squadForEditId, selectedSquad?.id)}
			onClick={handleSetPredictionForEdit}>
			<GroupName>
				{group}
				{position}
			</GroupName>{" "}
			<SquadFlag selectedSquad={selectedSquad} isEditing={isCanEditPrediction} />{" "}
			<SquadName>{isMobile ? selectedSquad?.shortName : selectedSquad?.name}</SquadName>
			{lockFirstSquad ? <LockImg className={i18n.dir()} src={lockImg} alt={"Lock"} /> : null}
			{getSwipeIcon(isLockChangeSquad, squadForEditId, selectedSquad?.id)}
		</PredictionWrapper>
	);
};
